import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import FormInput from "../../components/UI/FormInput";
import FormSelect from "../../components/UI/FormSelect";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import { createPatient } from "../../services/patientService";
import {
  CloseIcon,
  UserIcon,
  ShieldIcon,
  MapPinIcon,
  PhoneIcon,
  AlertCircleIcon,
  HelpingIcon,
  DoctorIcon,
  PillIcon,
  BuildingIcon,
} from "../../Icons";
import { toIsoDate } from "../../utils/dateUtils";
import { formatPhone } from "../Application/application.validation";
import ZipCodeInput from "./components/ZipCodeInput";
import CollapsibleSection from "./components/CollapsibleSection";
import {
  RowTextField,
  RowSelectField,
  RowDateField,
  RowBillableField,
  DiagnosisSearchField,
  PhysicianSearchField,
  AllergyRowFields,
  AddRowButton,
  RemoveRowButton,
} from "./components/MultiRowFields";
import InsuranceNameField from "./components/InsuranceNameField";
import {
  REFERRAL_SOURCE_OPTIONS,
  RELATIONSHIP_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RACE_OPTIONS,
  RELIGION_OPTIONS,
  PHYSICIAN_TYPE_OPTIONS,
  PECOS_STATUS_OPTIONS,
  pecosStatusFromPmd,
  PHARMACY_TYPE_OPTIONS,
  HOSPITAL_TYPE_OPTIONS,
  US_PHONE,
  US_ZIP,
  EMAIL_RE,
  parsePhysicianApiFieldErrors,
  formatZip,
  emptyDiagnosisAllergy,
  emptyAllergy,
  emptyReferral,
  emptyInsurance,
  emptyPhysician,
  emptyEmergencyContact,
  serializeAllergies,
  findIncompleteOtherAllergy,
  ALLERGY_OTHER_VALUE,
  COMMON_ALLERGY_OPTIONS,
  CLINICAL_GROUP_OPTIONS,
} from "./patientForm.shared";
import {
  emptyNutrition,
  serializeNutrition,
  validateNutritionFields,
  NUTRITION_SECTION_TITLE,
} from "./patientNutrition.shared";
import {
  NutritionDietEditFields,
  NutritionRestrictionEditFields,
} from "./components/NutritionSectionFields";
import {
  DiagnosisNutritionSubsection,
  DiagnosisNutritionSubsectionStack,
} from "./components/DiagnosisNutritionSubsection";
import { PATIENT_PAGE_STACK } from "./patientPageLayout";
import {
  PATIENT_STATUS_SELECT_OPTIONS,
  SOC_DATE_LABEL,
  EOC_DATE_LABEL,
  statusRequiresEocDate,
  isActiveCensusStatus,
} from "./components/StatusBadge";
import { patientSectionTones, PATIENT_THEME } from "./patientTheme";
import useDemographicsLibraryOptions from "./useDemographicsLibraryOptions";
import { PrimaryFacilityEditFields } from "./components/PrimaryFacilitySection";

/** Same required rules for Save as Draft and Save Patient (scalar fields only — arrays validated separately) */
const patientSchema = z
  .object({
    lastName: z.string().trim().min(1, "Last name is required"),
    firstName: z.string().trim().min(1, "First name is required"),
    middleName: z.string().optional(),
    dateOfBirth: z.string().trim().min(1, "Date of birth is required"),
    gender: z
      .string()
      .refine((v) => v === "Male" || v === "Female", {
        message: "Gender is required (Male or Female)",
      }),
    status: z.string().optional(),
    admissionDate: z.string().trim().min(1, "SOC date is required"),
    eocDate: z.string().optional(),
    language: z.string().optional(),
    maritalStatus: z.string().optional(),
    race: z.string().optional(),
    religion: z.string().optional(),
    email: z.string().email("Invalid email").optional().or(z.literal("")),
    phone: z
      .string()
      .trim()
      .min(1, "Primary phone is required")
      .regex(US_PHONE, "Primary phone must be (XXX)XXX-XXXX"),
    alternatePhone: z.string().optional(),
    address: z.string().trim().min(1, "Street address is required"),
    addressLine2: z.string().trim().min(1, "Apt / Suite is required"),
    city: z.string().optional(),
    state: z.string().trim().min(1, "State is required"),
    zip: z
      .string()
      .trim()
      .min(1, "Zip code is required")
      .regex(US_ZIP, "Zip must be XXXXX or XXXXX-XXXX"),
    pharmacyName: z.string().optional(),
    pharmacyAddress: z.string().optional(),
    pharmacyCity: z.string().optional(),
    pharmacyState: z.string().optional(),
    pharmacyZip: z.string().optional(),
    pharmacyPhone: z.string().optional(),
    pharmacyFax: z.string().optional(),
    pharmacyType: z.string().optional(),
    hospitalName: z.string().optional(),
    hospitalAddress: z.string().optional(),
    hospitalCity: z.string().optional(),
    hospitalState: z.string().optional(),
    hospitalZip: z.string().optional(),
    hospitalPhone: z.string().optional(),
    hospitalFax: z.string().optional(),
    hospitalType: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (statusRequiresEocDate(data.status) && !String(data.eocDate || "").trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["eocDate"],
        message: "EOC date is required when status is Inactive or Discharged",
      });
    }
    if (data.alternatePhone?.trim() && !US_PHONE.test(data.alternatePhone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["alternatePhone"],
        message: "Alternate phone must be (XXX)XXX-XXXX",
      });
    }
    if (data.pharmacyZip?.trim() && !US_ZIP.test(data.pharmacyZip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pharmacyZip"],
        message: "Zip must be XXXXX or XXXXX-XXXX",
      });
    }
    if (data.pharmacyPhone?.trim() && !US_PHONE.test(data.pharmacyPhone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pharmacyPhone"],
        message: "Phone must be (XXX)XXX-XXXX",
      });
    }
    if (data.pharmacyFax?.trim() && !US_PHONE.test(data.pharmacyFax)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pharmacyFax"],
        message: "Fax must be (XXX)XXX-XXXX",
      });
    }
    if (data.hospitalZip?.trim() && !US_ZIP.test(data.hospitalZip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["hospitalZip"],
        message: "Zip must be XXXXX or XXXXX-XXXX",
      });
    }
    if (data.hospitalPhone?.trim() && !US_PHONE.test(data.hospitalPhone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["hospitalPhone"],
        message: "Phone must be (XXX)XXX-XXXX",
      });
    }
    if (data.hospitalFax?.trim() && !US_PHONE.test(data.hospitalFax)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["hospitalFax"],
        message: "Fax must be (XXX)XXX-XXXX",
      });
    }
  });

const dateLabelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";
const dateInputClass = `block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none ${PATIENT_THEME.focus}`;

const SECTION_TONES = patientSectionTones([
  "personal",
  "contact",
  "diagnosis",
  "referral",
  "insurance",
  "physician",
  "pharmacy",
  "hospital",
  "emergency",
]);

const SectionCard = ({
  title,
  icon,
  tone = "personal",
  children,
  cols = "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5",
  badge,
}) => {
  const t = SECTION_TONES[tone] || SECTION_TONES.personal;
  return (
    <CollapsibleSection
      title={title}
      icon={icon}
      tone={t}
      badge={badge}
      defaultOpen
      bodyClassName={
        cols
          ? `p-4 grid grid-cols-1 ${cols} gap-x-3 gap-y-3`
          : "p-4 space-y-3"
      }
    >
      {children}
    </CollapsibleSection>
  );
};

/** Generic multi-row section body: renders rows + Add button, tone-aware. */
const MultiRowSection = ({ rows, renderRow, onAdd, onRemove, addLabel, tone, minRows = 0 }) => {
  const t = SECTION_TONES[tone] || SECTION_TONES.personal;
  return (
    <div className="space-y-3">
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-start border-b border-slate-100 last:border-0 pb-3 last:pb-0"
        >
          {renderRow(row, idx)}
          {rows.length > minRows && (
            <div className="lg:col-span-1 flex sm:justify-end self-end">
              <RemoveRowButton onClick={() => onRemove(idx)} label={`Remove row ${idx + 1}`} />
            </div>
          )}
        </div>
      ))}
      <AddRowButton label={addLabel} onClick={onAdd} tone={t.addTone} />
    </div>
  );
};

const PatientForm = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [diagnosisAllergies, setDiagnosisAllergies] = useState([emptyDiagnosisAllergy()]);
  const [allergies, setAllergies] = useState([emptyAllergy()]);
  const [nutrition, setNutrition] = useState(emptyNutrition());
  const [referrals, setReferrals] = useState([emptyReferral()]);
  const [insurances, setInsurances] = useState([emptyInsurance()]);
  const [physicians, setPhysicians] = useState([emptyPhysician()]);
  const [physicianFieldErrors, setPhysicianFieldErrors] = useState({});
  const [emergencyContacts, setEmergencyContacts] = useState([emptyEmergencyContact()]);

  const form = useForm({
    resolver: zodResolver(patientSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      status: "PreAdmit",
      language: "English",
      gender: "",
      maritalStatus: "",
      race: "",
      religion: "",
      dateOfBirth: "",
      admissionDate: "",
      eocDate: "",
      lastName: "",
      firstName: "",
      middleName: "",
      email: "",
      phone: "",
      alternatePhone: "",
      address: "",
      addressLine2: "",
      city: "",
      state: "",
      zip: "",
      pharmacyName: "",
      pharmacyAddress: "",
      pharmacyCity: "",
      pharmacyState: "",
      pharmacyZip: "",
      pharmacyPhone: "",
      pharmacyFax: "",
      pharmacyType: "",
      hospitalName: "",
      hospitalAddress: "",
      hospitalCity: "",
      hospitalState: "",
      hospitalZip: "",
      hospitalPhone: "",
      hospitalFax: "",
      hospitalType: "",
    },
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = form;

  const watchedStatus = useWatch({ control, name: "status" });
  const showEocDate = statusRequiresEocDate(watchedStatus);

  useEffect(() => {
    if (!showEocDate) {
      setValue("eocDate", "");
    }
  }, [showEocDate, setValue]);

  const {
    referralSourceOptions: libraryReferralOptions,
    findReferral,
  } = useDemographicsLibraryOptions();

  const ensureOption = (options, value) => {
    if (!value) return options;
    if (options.some((o) => o.value === value)) return options;
    return [...options, { value, label: value }];
  };

  const updateRow = (setter) => (idx, key, value) => {
    setter((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };
  const addRow = (setter, factory) => () => setter((prev) => [...prev, factory()]);
  const removeRow = (setter) => (idx) => setter((prev) => prev.filter((_, i) => i !== idx));

  const updateDiagnosis = (idx, key, value) => {
    setDiagnosisAllergies((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)),
    );
  };
  const selectDiagnosisFromCatalog = (idx, item) => {
    setDiagnosisAllergies((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              diagnosisCode: item.code || "",
              primaryDiagnosis: item.label || `${item.code} - ${item.description}`,
              clinicalGroup: item.clinicalGroup || row.clinicalGroup,
              isBillable:
                typeof item.isBillable === "boolean"
                  ? item.isBillable
                  : typeof item.primaryAllowed === "boolean"
                    ? item.primaryAllowed
                    : true,
            }
          : row,
      ),
    );
    if (idx === 0 && item.billingWarning) {
      toast.error(item.billingWarning, { duration: 5000 });
    } else if (item.requiresCodeFirst) {
      toast(item.billingWarning || "Code First required for this diagnosis", {
        icon: "⚠️",
        duration: 4000,
      });
    } else if (item.isBillable === false) {
      toast.error("Not billable as primary diagnosis", { duration: 4000 });
    }
  };
  const clearDiagnosisCatalog = (idx) => {
    setDiagnosisAllergies((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, diagnosisCode: "" } : row,
      ),
    );
  };
  const addDiagnosis = () => {
    setDiagnosisAllergies((prev) => [...prev, emptyDiagnosisAllergy()]);
  };
  const removeDiagnosis = removeRow(setDiagnosisAllergies);

  const updateAllergySelection = (idx, value) => {
    setAllergies((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              allergyName: value,
              otherText:
                value === ALLERGY_OTHER_VALUE ? row.otherText || "" : "",
            }
          : row,
      ),
    );
  };
  const updateAllergyOtherText = (idx, value) => {
    setAllergies((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, otherText: value } : row)),
    );
  };
  const addAllergy = () => {
    setAllergies((prev) => [...prev, emptyAllergy()]);
  };
  const removeAllergy = removeRow(setAllergies);

  const updateReferral = updateRow(setReferrals);
  const addReferral = addRow(setReferrals, emptyReferral);
  const removeReferral = removeRow(setReferrals);

  const updateInsurance = updateRow(setInsurances);
  const addInsurance = addRow(setInsurances, emptyInsurance);
  const removeInsurance = removeRow(setInsurances);

  const updatePhysician = (idx, key, value) => {
    setPhysicians((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)),
    );
    setPhysicianFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`physicians.${idx}.${key}`];
      return next;
    });
  };
  const selectPhysicianFromCatalog = (idx, item) => {
    setPhysicians((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              physicianName: item.displayName || row.physicianName,
              npiNumber: item.npi || "",
              pecosStatus: pecosStatusFromPmd(item.pmd),
              physicianAddress: item.fullAddress || item.address || row.physicianAddress,
              physicianPhone: item.phone || row.physicianPhone,
              physicianEmail: item.email || row.physicianEmail,
              physicianFax: item.fax || row.physicianFax,
            }
          : row,
      ),
    );
    setPhysicianFieldErrors((prev) => {
      const next = { ...prev };
      [
        "physicianName",
        "npiNumber",
        "pecosStatus",
        "physicianAddress",
        "physicianPhone",
        "physicianEmail",
        "physicianFax",
      ].forEach((k) => delete next[`physicians.${idx}.${k}`]);
      return next;
    });
  };
  const clearPhysicianCatalog = (idx) => {
    setPhysicians((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, npiNumber: "" } : row)),
    );
  };
  const addPhysician = addRow(setPhysicians, emptyPhysician);
  const removePhysician = removeRow(setPhysicians);

  const updateContact = updateRow(setEmergencyContacts);
  const addContact = addRow(setEmergencyContacts, emptyEmergencyContact);
  const removeContact = removeRow(setEmergencyContacts);

  const nonEmpty = (v) => Boolean(String(v || "").trim());

  const validatePhysicianFields = () => {
    const errors = {};
    for (let idx = 0; idx < physicians.length; idx += 1) {
      const p = physicians[idx];
      const touched =
        nonEmpty(p.physicianName) ||
        nonEmpty(p.physicianAddress) ||
        nonEmpty(p.physicianPhone) ||
        nonEmpty(p.physicianFax) ||
        nonEmpty(p.physicianEmail) ||
        nonEmpty(p.npiNumber) ||
        nonEmpty(p.physicianType);
      if (!touched) continue;
      if (!nonEmpty(p.physicianName)) {
        errors[`physicians.${idx}.physicianName`] =
          "Physician name is required";
      }
      if (p.physicianPhone?.trim() && !US_PHONE.test(p.physicianPhone)) {
        errors[`physicians.${idx}.physicianPhone`] =
          "Phone must be (XXX)XXX-XXXX";
      }
      if (p.physicianFax?.trim() && !US_PHONE.test(p.physicianFax)) {
        errors[`physicians.${idx}.physicianFax`] = "Fax must be (XXX)XXX-XXXX";
      }
      if (p.physicianEmail?.trim() && !EMAIL_RE.test(p.physicianEmail.trim())) {
        errors[`physicians.${idx}.physicianEmail`] = "Invalid email format";
      }
    }
    return errors;
  };

  const validateArrays = () => {
    const validInsurances = insurances.filter((i) => nonEmpty(i.insuranceName));
    if (!validInsurances.length) {
      return "At least one insurance is required";
    }
    for (let idx = 0; idx < validInsurances.length; idx += 1) {
      if (!nonEmpty(validInsurances[idx].policyNumber)) {
        return `Insurance ${idx + 1}: policy number is required`;
      }
    }
    for (let idx = 0; idx < emergencyContacts.length; idx += 1) {
      const c = emergencyContacts[idx];
      const touched = nonEmpty(c.firstName) || nonEmpty(c.lastName) || nonEmpty(c.phone);
      if (touched && (!nonEmpty(c.firstName) || !nonEmpty(c.lastName))) {
        return `Emergency contact ${idx + 1}: first and last name are required`;
      }
      if (c.phone?.trim() && !US_PHONE.test(c.phone)) {
        return `Emergency contact ${idx + 1}: phone must be (XXX)XXX-XXXX`;
      }
    }
    const incompleteAllergy = findIncompleteOtherAllergy(allergies);
    if (incompleteAllergy) {
      return incompleteAllergy.message;
    }
    const nutritionError = validateNutritionFields(nutrition);
    if (nutritionError) {
      return nutritionError;
    }
    return null;
  };

  const buildPayload = (data, asDraft) => {
    const status = asDraft ? "PreAdmit" : data.status || "PreAdmit";

    const cleanInsurances = insurances
      .filter((i) => nonEmpty(i.insuranceName))
      .map((i) => ({
        startDate: i.startDate ? toIsoDate(i.startDate) : undefined,
        insuranceName: i.insuranceName.trim(),
        policyNumber: nonEmpty(i.policyNumber) ? i.policyNumber.trim() : undefined,
        insuranceGroup: nonEmpty(i.insuranceGroup) ? i.insuranceGroup.trim() : undefined,
        patientCoPay: nonEmpty(i.patientCoPay) ? i.patientCoPay.trim() : undefined,
      }));

    const cleanPhysicians = physicians
      .filter((p) => nonEmpty(p.physicianName))
      .map((p) => ({
        physicianType: nonEmpty(p.physicianType) ? p.physicianType : undefined,
        physicianName: p.physicianName.trim(),
        physicianAddress: nonEmpty(p.physicianAddress) ? p.physicianAddress.trim() : undefined,
        npiNumber: nonEmpty(p.npiNumber) ? p.npiNumber.trim() : undefined,
        pecosStatus:
          p.pecosStatus === "enrolled" || p.pecosStatus === "un-enrolled"
            ? p.pecosStatus
            : undefined,
        physicianPhone: nonEmpty(p.physicianPhone) ? p.physicianPhone.trim() : undefined,
        physicianEmail: nonEmpty(p.physicianEmail) ? p.physicianEmail.trim() : undefined,
        physicianFax: nonEmpty(p.physicianFax) ? p.physicianFax.trim() : undefined,
      }));

    const cleanReferrals = referrals
      .filter((r) => nonEmpty(r.referralDate) || nonEmpty(r.referralSource) || nonEmpty(r.referralName))
      .map((r) => ({
        referralDate: nonEmpty(r.referralDate) ? toIsoDate(r.referralDate) : undefined,
        referralSource: nonEmpty(r.referralSource) ? r.referralSource.trim() : undefined,
        referralName: nonEmpty(r.referralName) ? r.referralName.trim() : undefined,
      }));

    const cleanDiagnosisAllergies = diagnosisAllergies
      .filter(
        (d) =>
          nonEmpty(d.primaryDiagnosis) ||
          nonEmpty(d.diagnosisCode) ||
          nonEmpty(d.clinicalGroup),
      )
      .map((d) => ({
        diagnosisCode: nonEmpty(d.diagnosisCode)
          ? d.diagnosisCode.trim()
          : undefined,
        primaryDiagnosis: nonEmpty(d.primaryDiagnosis)
          ? d.primaryDiagnosis.trim()
          : undefined,
        clinicalGroup: nonEmpty(d.clinicalGroup)
          ? d.clinicalGroup.trim()
          : undefined,
        isBillable:
          typeof d.isBillable === "boolean" ? d.isBillable : undefined,
      }));

    const cleanAllergies = serializeAllergies(allergies);

    const cleanEmergencyContacts = emergencyContacts
      .filter((c) => nonEmpty(c.firstName) && nonEmpty(c.lastName))
      .map((c) => ({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        relationship: nonEmpty(c.relationship) ? c.relationship.trim() : undefined,
        phone: nonEmpty(c.phone) ? c.phone.trim() : undefined,
      }));

    const firstInsurance = cleanInsurances[0];

    return {
      ...data,
      status,
      isActive: isActiveCensusStatus(status),
      middleName: data.middleName?.trim() ? data.middleName.trim() : null,
      dateOfBirth: data.dateOfBirth ? toIsoDate(data.dateOfBirth) : undefined,
      admissionDate: data.admissionDate ? toIsoDate(data.admissionDate) : undefined,
      eocDate: statusRequiresEocDate(status)
        ? data.eocDate
          ? toIsoDate(data.eocDate)
          : null
        : null,
      gender: data.gender || undefined,
      maritalStatus: data.maritalStatus || undefined,
      language: data.language?.trim() || null,
      race: data.race?.trim() || null,
      religion: data.religion?.trim() || null,
      zip: data.zip ? formatZip(data.zip) : undefined,
      pharmacyName: data.pharmacyName?.trim() || undefined,
      pharmacyAddress: data.pharmacyAddress?.trim() || undefined,
      pharmacyCity: data.pharmacyCity?.trim() || undefined,
      pharmacyState: data.pharmacyState?.trim() || undefined,
      pharmacyZip: data.pharmacyZip ? formatZip(data.pharmacyZip) : undefined,
      pharmacyPhone: data.pharmacyPhone?.trim() || undefined,
      pharmacyFax: data.pharmacyFax?.trim() || undefined,
      pharmacyType: data.pharmacyType?.trim() || undefined,
      hospitalName: data.hospitalName?.trim() || undefined,
      hospitalAddress: data.hospitalAddress?.trim() || undefined,
      hospitalCity: data.hospitalCity?.trim() || undefined,
      hospitalState: data.hospitalState?.trim() || undefined,
      hospitalZip: data.hospitalZip ? formatZip(data.hospitalZip) : undefined,
      hospitalPhone: data.hospitalPhone?.trim() || undefined,
      hospitalFax: data.hospitalFax?.trim() || undefined,
      hospitalType: data.hospitalType?.trim() || undefined,
      // Legacy flat fields kept in sync with the first row for backward compatibility.
      insurance: firstInsurance?.insuranceName,
      insuranceNumber: firstInsurance?.policyNumber,
      diagnosisAllergies: cleanDiagnosisAllergies,
      allergies: cleanAllergies,
      nutrition: serializeNutrition(nutrition),
      referrals: cleanReferrals,
      insurances: cleanInsurances,
      physicians: cleanPhysicians,
      emergencyContacts: cleanEmergencyContacts,
    };
  };

  const onSubmit = async (data, asDraft = false) => {
    const physicianErrors = validatePhysicianFields();
    setPhysicianFieldErrors(physicianErrors);
    if (Object.keys(physicianErrors).length) {
      toast.error(Object.values(physicianErrors)[0]);
      return;
    }
    const arrayError = validateArrays();
    if (arrayError) {
      toast.error(arrayError);
      return;
    }
    setSubmitting(true);
    try {
      await createPatient(buildPayload(data, asDraft));
      toast.success(asDraft ? "Draft saved" : "Patient saved successfully");
      navigate("/patients");
    } catch (err) {
      const apiFieldErrors = parsePhysicianApiFieldErrors(err.message);
      if (Object.keys(apiFieldErrors).length) {
        setPhysicianFieldErrors(apiFieldErrors);
      }
      toast.error(err.message || "Failed to save patient");
    } finally {
      setSubmitting(false);
    }
  };

  const onInvalid = (formErrors) => {
    const firstKey = Object.keys(formErrors)[0];
    const firstMsg = formErrors[firstKey]?.message;
    toast.error(firstMsg || "Please fill all required fields");
    if (firstKey) {
      const el = document.querySelector(`[name="${firstKey}"]`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      el?.focus?.();
    }
  };

  return (
    <div className={`relative ${PATIENT_PAGE_STACK}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 pr-1">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-slate-800">Add New Patient</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Create a clinical record · (*) required · dates mm/dd/yyyy · zip
            XXXXX-XXXX
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit((d) => onSubmit(d, true), onInvalid)}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed box-border"
          >
            {submitting ? (
              <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : null}
            Save as Draft
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit((d) => onSubmit(d, false), onInvalid)}
            className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed box-border border ${PATIENT_THEME.button.outline}`}
          >
            {submitting ? (
              <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : null}
            Save Patient
          </button>
          <button
            type="button"
            onClick={() => navigate("/patients")}
            className="inline-flex items-center justify-center h-9 w-9 shrink-0 box-border bg-white rounded-md border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-colors"
            aria-label="Close form"
            title="Close"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      </div>

      <FormProvider {...form}>
        <form
          className="space-y-4"
          onSubmit={handleSubmit((d) => onSubmit(d, false), onInvalid)}
          noValidate
        >
          <SectionCard
            title="Personal Information"
            icon={<UserIcon size={14} />}
            tone="personal"
          >
            <FormSelect
              size="sm"
              label="Status"
              name="status"
              register={register}
              options={PATIENT_STATUS_SELECT_OPTIONS}
            />
            <InputDatePicker
              label={SOC_DATE_LABEL}
              name="admissionDate"
              formatSeparator="/"
              allowManualEntry
              isRequired
              labelClassName={dateLabelClass}
              inputClassName={dateInputClass}
            />
            {showEocDate ? (
              <InputDatePicker
                label={EOC_DATE_LABEL}
                name="eocDate"
                formatSeparator="/"
                allowManualEntry
                isRequired
                labelClassName={dateLabelClass}
                inputClassName={dateInputClass}
              />
            ) : null}
            <FormInput
              size="sm"
              label="Last Name"
              name="lastName"
              register={register}
              error={errors.lastName}
              required
              placeholder="Smith"
            />
            <FormInput
              size="sm"
              label="First Name"
              name="firstName"
              register={register}
              error={errors.firstName}
              required
              placeholder="Johnathan"
            />
            <FormInput
              size="sm"
              label="Middle Name"
              name="middleName"
              register={register}
              error={errors.middleName}
              placeholder="Optional"
            />
            <InputDatePicker
              label="Date of Birth"
              name="dateOfBirth"
              formatSeparator="/"
              allowManualEntry
              isRequired
              labelClassName={dateLabelClass}
              inputClassName={dateInputClass}
            />
            <FormSelect
              size="sm"
              label="Gender"
              name="gender"
              register={register}
              error={errors.gender}
              required
              placeholder="Select"
              options={[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ]}
            />
            <FormInput
              size="sm"
              label="Primary Language"
              name="language"
              register={register}
              error={errors.language}
            />
            <FormSelect
              size="sm"
              label="Marital Status"
              name="maritalStatus"
              register={register}
              placeholder="Select"
              options={MARITAL_STATUS_OPTIONS}
            />
            <FormSelect
              size="sm"
              label="Race"
              name="race"
              register={register}
              placeholder="Select"
              options={RACE_OPTIONS}
            />
            <FormSelect
              size="sm"
              label="Religion"
              name="religion"
              register={register}
              placeholder="Select"
              options={RELIGION_OPTIONS}
            />
          </SectionCard>

          <SectionCard
            title="Contact & Location"
            icon={<MapPinIcon size={14} />}
            tone="contact"
            cols="sm:grid-cols-3"
          >
            <FormInput
              size="sm"
              label="Primary Phone"
              name="phone"
              register={register}
              error={errors.phone}
              required
              placeholder="(XXX)XXX-XXXX"
              maxLength={13}
              onInput={(e) => {
                e.target.value = formatPhone(e.target.value);
              }}
            />
            <FormInput
              size="sm"
              label="Alternate Phone"
              name="alternatePhone"
              register={register}
              error={errors.alternatePhone}
              placeholder="(XXX)XXX-XXXX"
              maxLength={13}
              onInput={(e) => {
                e.target.value = formatPhone(e.target.value);
              }}
            />
            <FormInput
              size="sm"
              label="Email"
              name="email"
              type="email"
              register={register}
              error={errors.email}
            />
            <FormInput
              size="sm"
              label="Street Address"
              name="address"
              register={register}
              error={errors.address}
              required
            />
            <FormInput
              size="sm"
              label="Apt / Suite"
              name="addressLine2"
              register={register}
              error={errors.addressLine2}
              required
            />
            <FormInput
              size="sm"
              label="City"
              name="city"
              register={register}
              error={errors.city}
            />
            <FormInput
              size="sm"
              label="State"
              name="state"
              register={register}
              error={errors.state}
              required
            />
            <Controller
              name="zip"
              control={control}
              render={({ field }) => (
                <ZipCodeInput
                  label="Zip Code"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.zip?.message}
                  required
                />
              )}
            />
          </SectionCard>

          <SectionCard
            title={NUTRITION_SECTION_TITLE}
            icon={<AlertCircleIcon size={14} />}
            tone="diagnosis"
            cols={null}
          >
            <DiagnosisNutritionSubsectionStack>
              <DiagnosisNutritionSubsection title="Diagnosis">
                <MultiRowSection
                  tone="diagnosis"
                  rows={diagnosisAllergies}
                  onAdd={addDiagnosis}
                  onRemove={removeDiagnosis}
                  addLabel="Add Diagnosis"
                  minRows={1}
                  renderRow={(row, idx) => (
                    <>
                      <DiagnosisSearchField
                        label={
                          idx === 0
                            ? "Primary Diagnosis"
                            : "Secondary Diagnosis"
                        }
                        value={row.primaryDiagnosis}
                        diagnosisCode={row.diagnosisCode}
                        onChangeText={(text) =>
                          updateDiagnosis(idx, "primaryDiagnosis", text)
                        }
                        onSelectCatalog={(item) =>
                          selectDiagnosisFromCatalog(idx, item)
                        }
                        onClearCatalog={() => clearDiagnosisCatalog(idx)}
                        placeholder="Type code or name (e.g. dia, E11.9)…"
                        className="lg:col-span-6"
                      />
                      <RowSelectField
                        label="Clinical Group"
                        value={row.clinicalGroup}
                        onChange={(e) =>
                          updateDiagnosis(
                            idx,
                            "clinicalGroup",
                            e.target.value,
                          )
                        }
                        options={CLINICAL_GROUP_OPTIONS}
                        placeholder="Select clinical group"
                        className="lg:col-span-3"
                      />
                      <RowBillableField
                        label="Billable"
                        checked={row.isBillable === true}
                        onChange={(checked) =>
                          updateDiagnosis(idx, "isBillable", checked)
                        }
                        className="lg:col-span-2"
                      />
                    </>
                  )}
                />
              </DiagnosisNutritionSubsection>

              <DiagnosisNutritionSubsection title="Allergies">
                <MultiRowSection
                  tone="diagnosis"
                  rows={allergies}
                  onAdd={addAllergy}
                  onRemove={removeAllergy}
                  addLabel="Add Allergy"
                  minRows={0}
                  renderRow={(row, idx) => (
                    <AllergyRowFields
                      row={row}
                      index={idx}
                      options={COMMON_ALLERGY_OPTIONS}
                      onSelectChange={(value) =>
                        updateAllergySelection(idx, value)
                      }
                      onOtherTextChange={(value) =>
                        updateAllergyOtherText(idx, value)
                      }
                    />
                  )}
                />
              </DiagnosisNutritionSubsection>

              <DiagnosisNutritionSubsection title="Diet">
                <NutritionDietEditFields
                  nutrition={nutrition}
                  onChange={setNutrition}
                />
              </DiagnosisNutritionSubsection>

              <DiagnosisNutritionSubsection title="Restriction">
                <NutritionRestrictionEditFields
                  nutrition={nutrition}
                  onChange={setNutrition}
                />
              </DiagnosisNutritionSubsection>
            </DiagnosisNutritionSubsectionStack>
          </SectionCard>

          <SectionCard
            title="Referral Info"
            icon={<HelpingIcon size={14} />}
            tone="referral"
            cols={null}
          >
            <MultiRowSection
              tone="referral"
              rows={referrals}
              onAdd={addReferral}
              onRemove={removeReferral}
              addLabel="Add Referral"
              renderRow={(row, idx) => (
                <>
                  <RowDateField
                    label="Referral Date"
                    value={row.referralDate}
                    onChange={(v) => updateReferral(idx, "referralDate", v)}
                    className="lg:col-span-3"
                  />
                  <RowSelectField
                    label="Referral Source"
                    value={row.referralSource}
                    onChange={(e) => {
                      const next = e.target.value;
                      updateReferral(idx, "referralSource", next);
                      const match = findReferral(next);
                      if (match?.referralName) {
                        updateReferral(idx, "referralName", match.referralName);
                      }
                    }}
                    options={ensureOption(
                      libraryReferralOptions.length
                        ? libraryReferralOptions
                        : REFERRAL_SOURCE_OPTIONS,
                      row.referralSource,
                    )}
                    className="lg:col-span-4"
                  />
                  <RowTextField
                    label="Referral Name"
                    value={row.referralName}
                    onChange={(e) => updateReferral(idx, "referralName", e.target.value)}
                    className="lg:col-span-4"
                  />
                </>
              )}
            />
          </SectionCard>

          <SectionCard
            title="Insurance Info"
            icon={<ShieldIcon size={14} />}
            tone="insurance"
            cols={null}
          >
            <MultiRowSection
              tone="insurance"
              rows={insurances}
              onAdd={addInsurance}
              onRemove={removeInsurance}
              addLabel="Add Insurance"
              minRows={1}
              renderRow={(row, idx) => (
                <>
                  <RowDateField
                    label="Start Date"
                    value={row.startDate}
                    onChange={(v) => updateInsurance(idx, "startDate", v)}
                    className="lg:col-span-2"
                  />
                  <InsuranceNameField
                    key={`insurance-name-${idx}`}
                    label="Insurance Name"
                    value={row.insuranceName}
                    onChange={(v) => updateInsurance(idx, "insuranceName", v)}
                    required
                    className="lg:col-span-3"
                  />
                  <RowTextField
                    label="Policy #"
                    value={row.policyNumber}
                    onChange={(e) => updateInsurance(idx, "policyNumber", e.target.value)}
                    required
                    className="lg:col-span-2"
                  />
                  <RowTextField
                    label="Group"
                    value={row.insuranceGroup}
                    onChange={(e) => updateInsurance(idx, "insuranceGroup", e.target.value)}
                    className="lg:col-span-2"
                  />
                  <RowTextField
                    label="Co-Pay"
                    value={row.patientCoPay}
                    onChange={(e) => updateInsurance(idx, "patientCoPay", e.target.value)}
                    className="lg:col-span-2"
                  />
                </>
              )}
            />
          </SectionCard>

          <SectionCard
            title="Physician Info"
            icon={<DoctorIcon size={14} />}
            tone="physician"
            cols={null}
          >
            <MultiRowSection
              tone="physician"
              rows={physicians}
              onAdd={addPhysician}
              onRemove={removePhysician}
              addLabel="Add Physician"
              renderRow={(row, idx) => (
                <>
                  <RowSelectField
                    label="Type"
                    value={row.physicianType}
                    onChange={(e) => updatePhysician(idx, "physicianType", e.target.value)}
                    options={PHYSICIAN_TYPE_OPTIONS}
                    className="lg:col-span-2"
                    error={physicianFieldErrors[`physicians.${idx}.physicianType`]}
                  />
                  <PhysicianSearchField
                    label="Physician Name"
                    value={row.physicianName}
                    npiNumber={row.npiNumber}
                    onChangeText={(text) =>
                      updatePhysician(idx, "physicianName", text)
                    }
                    onSelectCatalog={(item) =>
                      selectPhysicianFromCatalog(idx, item)
                    }
                    onClearCatalog={() => clearPhysicianCatalog(idx)}
                    placeholder="Search by name or NPI number…"
                    className="lg:col-span-3"
                    required
                    error={physicianFieldErrors[`physicians.${idx}.physicianName`]}
                  />
                  <RowTextField
                    label="Address"
                    value={row.physicianAddress}
                    onChange={(e) => updatePhysician(idx, "physicianAddress", e.target.value)}
                    className="lg:col-span-4"
                    error={physicianFieldErrors[`physicians.${idx}.physicianAddress`]}
                  />
                  <PhysicianSearchField
                    label="NPI Number"
                    mode="npi"
                    value={row.npiNumber}
                    onChangeText={(text) =>
                      updatePhysician(idx, "npiNumber", text)
                    }
                    onSelectCatalog={(item) =>
                      selectPhysicianFromCatalog(idx, item)
                    }
                    placeholder="Search by NPI number…"
                    className="lg:col-span-2"
                    error={physicianFieldErrors[`physicians.${idx}.npiNumber`]}
                  />
                  <RowSelectField
                    label="PECOS Status"
                    value={row.pecosStatus}
                    onChange={(e) =>
                      updatePhysician(idx, "pecosStatus", e.target.value)
                    }
                    options={PECOS_STATUS_OPTIONS}
                    placeholder="Select"
                    className="lg:col-span-3"
                    error={physicianFieldErrors[`physicians.${idx}.pecosStatus`]}
                  />
                  <RowTextField
                    label="Phone"
                    value={row.physicianPhone}
                    onChange={(e) => updatePhysician(idx, "physicianPhone", formatPhone(e.target.value))}
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    className="lg:col-span-3"
                    error={physicianFieldErrors[`physicians.${idx}.physicianPhone`]}
                  />
                  <RowTextField
                    label="Fax"
                    value={row.physicianFax}
                    onChange={(e) => updatePhysician(idx, "physicianFax", formatPhone(e.target.value))}
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    className="lg:col-span-3"
                    error={physicianFieldErrors[`physicians.${idx}.physicianFax`]}
                  />
                </>
              )}
            />
          </SectionCard>

          <SectionCard
            title="Primary Pharmacy"
            icon={<PillIcon size={14} />}
            tone="pharmacy"
            cols={null}
          >
            <PrimaryFacilityEditFields
              variant="pharmacy"
              control={control}
              register={register}
              setValue={setValue}
              errors={errors}
              typeOptions={PHARMACY_TYPE_OPTIONS}
            />
          </SectionCard>

          <SectionCard
            title="Primary Hospital"
            icon={<BuildingIcon size={14} />}
            tone="hospital"
            cols={null}
          >
            <PrimaryFacilityEditFields
              variant="hospital"
              control={control}
              register={register}
              setValue={setValue}
              errors={errors}
              typeOptions={HOSPITAL_TYPE_OPTIONS}
            />
          </SectionCard>

          <SectionCard
            title="Emergency Contact"
            icon={<PhoneIcon size={14} />}
            tone="emergency"
            cols={null}
          >
            <MultiRowSection
              tone="emergency"
              rows={emergencyContacts}
              onAdd={addContact}
              onRemove={removeContact}
              addLabel="Add Contact"
              renderRow={(row, idx) => (
                <>
                  <RowTextField
                    label="Last Name"
                    value={row.lastName}
                    onChange={(e) => updateContact(idx, "lastName", e.target.value)}
                    className="lg:col-span-3"
                  />
                  <RowTextField
                    label="First Name"
                    value={row.firstName}
                    onChange={(e) => updateContact(idx, "firstName", e.target.value)}
                    className="lg:col-span-3"
                  />
                  <RowSelectField
                    label="Relation"
                    value={row.relationship}
                    onChange={(e) => updateContact(idx, "relationship", e.target.value)}
                    options={RELATIONSHIP_OPTIONS}
                    className="lg:col-span-2"
                  />
                  <RowTextField
                    label="Phone Number"
                    value={row.phone}
                    onChange={(e) => updateContact(idx, "phone", formatPhone(e.target.value))}
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    className="lg:col-span-3"
                  />
                </>
              )}
            />
          </SectionCard>
        </form>
      </FormProvider>
    </div>
  );
};

export default PatientForm;
